import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@myphx-engineering/phx-design-system';

import {
    type PatientInfoFragment,
    RequestPrescriberOrderModificationDocument,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';

export const RequestSwitchButton = ({
    orderId,
    patient,
    targetNdc,
}: {
    orderId: string;
    patient: PatientInfoFragment;
    targetNdc?: string;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { enableUpdatedDrugSwitchLilly } = useFlags();

    const {
        refetch,
        isComplete: isRefetchComplete,
        isLoading: isRefetchLoading,
    } = useRefetchQueries(getRefetchQueryForPrescription(orderId, patient.id));

    const confirmationRoute = useMemo(() => {
        if (enableUpdatedDrugSwitchLilly) {
            return getAbsoluteRoute(
                'order.prescriberOrderId.switch.target.confirmation',
                {
                    prescriberOrderId: orderId,
                    targetNdc: targetNdc!,
                }
            );
        }

        return getAbsoluteRoute('order.prescriberOrderId.switch.verify', {
            prescriberOrderId: orderId,
        });
    }, [orderId, targetNdc, enableUpdatedDrugSwitchLilly]);

    useEffect(() => {
        if (isRefetchComplete) {
            navigate(confirmationRoute);
        }
    }, [navigate, isRefetchComplete, confirmationRoute]);

    const [RequestPrescriptionModification, { loading: isSwitchLoading }] =
        useMutation(RequestPrescriberOrderModificationDocument);

    const handleSwitch = useCallback(async () => {
        const result = await RequestPrescriptionModification({
            variables: {
                prescriberOrderId: orderId,
                putPrescriptionOnHold: true,
            },
        });

        if (result.data?.requestPrescriberOrderModification.success) {
            void refetch();
        }

        // TODO: Handle error scenarios, tell user to contact support if necessary
    }, [orderId, refetch, RequestPrescriptionModification]);

    return (
        <Button
            size="lg"
            onClick={handleSwitch}
            loading={isSwitchLoading || isRefetchLoading}
        >
            {t('switchPage.switchPrescription')}
        </Button>
    );
};
